import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  IconButton,
  Card,
  CardContent,
  CardActions,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Autocomplete,
  SelectChangeEvent
} from '@mui/material';
import {
  FilterAlt as FilterIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PlayArrow as RunIcon,
  Description as FileIcon,
  Image as ImageIcon,
  Movie as VideoIcon,
  AudioFile as AudioIcon,
  Archive as ArchiveIcon,
  Code as CodeIcon,
  Label as TagIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store';
import PageHeader from '../components/ui/PageHeader';
import { SmartFilter, FilterCriteria } from '../types/filters';
import filterService from '../services/filterService';
import fileService from '../services/fileService';
import usePageTitle from '../hooks/usePageTitle';

// Define types for form data
interface FilterFormData {
  name: string;
  description: string;
  fileTypes: string[];
  timeRangeValue: string;
  timeRangeUnit: 'days' | 'weeks' | 'months';
  sizeMin: string;
  sizeUnit: 'KB' | 'MB' | 'GB';
  tags: string[];
}

const Filters: React.FC = () => {
  usePageTitle('Smart Filters');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // State
  const [filters, setFilters] = useState<SmartFilter[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [editingFilter, setEditingFilter] = useState<SmartFilter | null>(null);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [formData, setFormData] = useState<FilterFormData>({
    name: '',
    description: '',
    fileTypes: [],
    timeRangeValue: '',
    timeRangeUnit: 'days',
    sizeMin: '',
    sizeUnit: 'MB',
    tags: []
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    filterId: ''
  });
  
  // Constants
  const fileTypeOptions = [
    { value: 'document', label: 'Documents', icon: <FileIcon /> },
    { value: 'image', label: 'Images', icon: <ImageIcon /> },
    { value: 'video', label: 'Videos', icon: <VideoIcon /> },
    { value: 'audio', label: 'Audio', icon: <AudioIcon /> },
    { value: 'archive', label: 'Archives', icon: <ArchiveIcon /> },
    { value: 'code', label: 'Code', icon: <CodeIcon /> }
  ];
  
  // Fetch filters data on component mount
  useEffect(() => {
    fetchFilters();
    fetchAvailableTags();
  }, []);
  
  const fetchFilters = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const filtersData = await filterService.getFilters();
      setFilters(filtersData);
    } catch (err: any) {
      setError(err.message || 'Failed to load filters');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAvailableTags = async () => {
    try {
      const tags = await filterService.getFilterTags();
      setAvailableTags(tags);
    } catch (error) {
      // Don't show error notification for this non-critical feature
      // Just set empty array to prevent UI errors
      setAvailableTags([]);
    }
  };
  
  const createDefaultFilters = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const defaultFilters = await filterService.createDefaultFilters();
      setFilters(prevFilters => [...prevFilters, ...defaultFilters]);
      
      // Show success notification
      // This assumes you have a notification system; adjust as needed
    } catch (err: any) {
      setError(err.message || 'Failed to create default filters');
    } finally {
      setLoading(false);
    }
  };
  
  const handleOpenDialog = (filter?: SmartFilter) => {
    if (filter) {
      // Edit existing filter
      setEditingFilter(filter);
      setFormData({
        name: filter.name,
        description: filter.description,
        fileTypes: filter.criteria.fileTypes || [],
        timeRangeValue: filter.criteria.timeRange?.value?.toString() || '',
        timeRangeUnit: (['days', 'weeks', 'months'] as const).includes(filter.criteria.timeRange?.unit as any)
          ? filter.criteria.timeRange?.unit as 'days' | 'weeks' | 'months'
          : 'days',
        sizeMin: filter.criteria.sizeRange?.min?.toString() || '',
        sizeUnit: (['KB', 'MB', 'GB'] as const).includes(filter.criteria.sizeRange?.unit as any)
          ? filter.criteria.sizeRange?.unit as 'KB' | 'MB' | 'GB'
          : 'MB',
        tags: filter.criteria.tags || []
      });
    } else {
      // Reset form for new filter
      setEditingFilter(null);
      setFormData({
        name: '',
        description: '',
        fileTypes: [],
        timeRangeValue: '',
        timeRangeUnit: 'days',
        sizeMin: '',
        sizeUnit: 'MB',
        tags: []
      });
    }
    
    setOpenFilterDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenFilterDialog(false);
    setEditingFilter(null);
  };
  
  const handleFormChange = (name: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSaveFilter = async () => {
    // Validate form
    if (!formData.name.trim()) {
      setError('Filter name is required');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Build filter criteria
      const filterCriteria: FilterCriteria = {
        fileTypes: formData.fileTypes,
        timeRange: formData.timeRangeValue ? { 
          value: parseInt(formData.timeRangeValue), 
          unit: formData.timeRangeUnit 
        } : null,
        sizeRange: formData.sizeMin ? { 
          min: parseInt(formData.sizeMin), 
          unit: formData.sizeUnit 
        } : null,
        tags: formData.tags,
        modifiedBy: null
      };
      
      let savedFilter: SmartFilter;
      
      if (editingFilter) {
        // Update existing filter
        savedFilter = await filterService.updateFilter(editingFilter.id, {
          name: formData.name,
          description: formData.description,
          criteria: filterCriteria
        });
        
        // Update local state
        setFilters(prevFilters => 
          prevFilters.map(f => f.id === editingFilter.id ? savedFilter : f)
        );
      } else {
        // Create new filter
        savedFilter = await filterService.createFilter({
          name: formData.name,
          description: formData.description,
          criteria: filterCriteria
        });
        
        // Update local state
        setFilters(prevFilters => [...prevFilters, savedFilter]);
      }
      
      handleCloseDialog();
    } catch (err: any) {
      setError(err.message || 'Failed to save filter');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteFilter = (filterId: string) => {
    setDeleteConfirmation({ open: true, filterId });
  };
  
  const confirmDeleteFilter = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await filterService.deleteFilter(deleteConfirmation.filterId);
      
      // Update local state
      setFilters(prevFilters => prevFilters.filter(f => f.id !== deleteConfirmation.filterId));
      
      setDeleteConfirmation({ open: false, filterId: '' });
    } catch (err: any) {
      setError(err.message || 'Failed to delete filter');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRunFilter = async (filter: SmartFilter) => {
    try {
      // Get matching files
      const result = await filterService.runFilter(filter.id);
      
      // Navigate to files view with filter applied
      navigate(`/files?filter=${filter.id}`);
    } catch (err) {
      setError('Failed to run filter');
    }
  };
  
  // Helper for file type icons
  const getFileTypeIcon = (type: string) => {
    switch (type) {
      case 'document':
        return <FileIcon />;
      case 'image':
        return <ImageIcon />;
      case 'video':
        return <VideoIcon />;
      case 'audio':
        return <AudioIcon />;
      case 'archive':
        return <ArchiveIcon />;
      case 'code':
        return <CodeIcon />;
      default:
        return <FileIcon />;
    }
  };
  
  // Helper to format criteria for display
  const formatCriteria = (criteria: FilterCriteria) => {
    const parts = [];

    // File types
    if (criteria.fileTypes && criteria.fileTypes.length > 0) {
      const fileTypeLabels = criteria.fileTypes.map(
        type => fileTypeOptions.find(opt => opt.value === type)?.label || type
      );
      parts.push(`File types: ${fileTypeLabels.join(', ')}`);
    }

    // Time range
    if (criteria.timeRange && criteria.timeRange.value) {
      parts.push(`Modified within: ${criteria.timeRange.value} ${criteria.timeRange.unit}`);
    }

    // Size range
    if (criteria.sizeRange && criteria.sizeRange.min) {
      parts.push(`Size: > ${criteria.sizeRange.min} ${criteria.sizeRange.unit}`);
    }

    // Tags
    if (criteria.tags && criteria.tags.length > 0) {
      parts.push(`Tags: ${criteria.tags.join(', ')}`);
    }

    // Modified by
    if (criteria.modifiedBy) {
      parts.push(`Modified by: ${criteria.modifiedBy}`);
    }

    // If no criteria specified
    if (parts.length === 0) {
      return <Typography variant="body2" color="text.secondary">No specific criteria defined</Typography>;
    }

    // Format as bullet points
    return (
      <Box component="ul" sx={{ pl: 2, m: 0 }}>
        {parts.map((part, index) => (
          <Typography key={index} component="li" variant="body2" sx={{ mb: 0.5 }}>
            {part}
          </Typography>
        ))}
      </Box>
    );
  };
  
  return (
    <Box sx={{ p: 3, maxWidth: '100%' }}>
      <PageHeader
        title="Smart Filters"
        icon={<FilterIcon fontSize="large" color="primary" />}
        description="Create and manage custom filters to quickly find your files"
        actions={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Create Filter
            </Button>
            {filters.length === 0 && (
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={createDefaultFilters}
              >
                Use Default Filters
              </Button>
            )}
          </Box>
        }
      />
      
      {loading && filters.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {filters.length === 0 ? (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 600, mx: 'auto', mt: 4 }}>
                <FilterIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  No filters yet
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Create smart filters to organize and quickly find your files based on criteria like file type, date, size, and tags.
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="center" mt={3}>
                  <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<AddIcon />} 
                    onClick={() => handleOpenDialog()}
                  >
                    Create Filter
                  </Button>
                  <Button 
                    variant="outlined" 
                    startIcon={<FilterIcon />} 
                    onClick={createDefaultFilters}
                  >
                    Use Default Filters
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          ) : (
            <Grid container spacing={3} sx={{ mt: 2 }}>
              {filters.map(filter => (
                <Grid item xs={12} sm={6} md={4} key={filter.id}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <FilterIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
                          {filter.name}
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFilter(filter.id);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      
                      {filter.description && (
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {filter.description}
                        </Typography>
                      )}
                      
                      <Divider sx={{ my: 1.5 }} />
                      
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Criteria:
                      </Typography>
                      
                      <Box>
                        {formatCriteria(filter.criteria)}
                      </Box>
                      
                      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {filter.criteria.fileTypes && filter.criteria.fileTypes.map(type => (
                          <Chip 
                            key={type} 
                            label={fileTypeOptions.find(opt => opt.value === type)?.label || type} 
                            size="small" 
                            icon={getFileTypeIcon(type)}
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                        {filter.criteria.tags && filter.criteria.tags.map(tag => (
                          <Chip 
                            key={tag} 
                            label={tag} 
                            size="small" 
                            icon={<TagIcon fontSize="small" />}
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </Box>
                      
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Matching files: <strong>{filter.matchCount || 0}</strong>
                        </Typography>
                      </Box>
                    </CardContent>
                    
                    <CardActions>
                      <Button 
                        size="small" 
                        color="primary"
                        startIcon={<RunIcon />}
                        onClick={() => handleRunFilter(filter)}
                      >
                        Run Filter
                      </Button>
                      <Box sx={{ flexGrow: 1 }} />
                      <Button 
                        size="small" 
                        startIcon={<EditIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDialog(filter);
                        }}
                      >
                        Edit
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      )}
      
      {/* New/Edit Filter Dialog */}
      <Dialog 
        open={openFilterDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingFilter ? 'Edit Filter' : 'Create New Filter'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Define criteria to quickly find files matching specific conditions.
          </DialogContentText>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                label="Filter Name"
                fullWidth
                required
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Filter Criteria
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="file-type-label">File Types</InputLabel>
                <Select
                  labelId="file-type-label"
                  id="file-type-select"
                  multiple
                  value={formData.fileTypes}
                  onChange={(e) => handleFormChange('fileTypes', e.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip 
                          key={value} 
                          label={fileTypeOptions.find(opt => opt.value === value)?.label || value}
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                >
                  {fileTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ mr: 1 }}>{option.icon}</Box>
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Modified Within (Time Value)"
                type="number"
                fullWidth
                value={formData.timeRangeValue}
                onChange={(e) => handleFormChange('timeRangeValue', e.target.value)}
                InputProps={{ inputProps: { min: 1 } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Time Unit</InputLabel>
                <Select
                  value={formData.timeRangeUnit}
                  onChange={(e: SelectChangeEvent) => 
                    handleFormChange('timeRangeUnit', e.target.value)
                  }
                  disabled={!formData.timeRangeValue}
                >
                  <MenuItem value="days">Days</MenuItem>
                  <MenuItem value="weeks">Weeks</MenuItem>
                  <MenuItem value="months">Months</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Minimum Size"
                type="number"
                fullWidth
                value={formData.sizeMin}
                onChange={(e) => handleFormChange('sizeMin', e.target.value)}
                InputProps={{ inputProps: { min: 1 } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Size Unit</InputLabel>
                <Select
                  value={formData.sizeUnit}
                  onChange={(e: SelectChangeEvent) => 
                    handleFormChange('sizeUnit', e.target.value as 'KB' | 'MB' | 'GB')
                  }
                  disabled={!formData.sizeMin}
                >
                  <MenuItem value="KB">KB</MenuItem>
                  <MenuItem value="MB">MB</MenuItem>
                  <MenuItem value="GB">GB</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                options={availableTags}
                value={formData.tags}
                onChange={(_, newValue) => handleFormChange('tags', newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags"
                    placeholder="Add tags"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip 
                      icon={<TagIcon />}
                      label={option} 
                      {...getTagProps({ index })}
                      size="small"
                    />
                  ))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveFilter} 
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Filter'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, filterId: '' })}
      >
        <DialogTitle>Delete Filter</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this filter? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmation({ open: false, filterId: '' })}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteFilter} 
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Filters;
export {}; 